import React, { useEffect, useState } from "react";
import {
  Menu,
  X,
  Home,
  User,
  Briefcase,
  FolderKanban,
  Star,
  Phone,
  MessageCircle,
  ChevronRight,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import logo from "../image/logo.png";

function Navbar() {
  const [activeSection, setActiveSection] = useState("home");
  const [menuOpen, setMenuOpen] = useState(false);

  const menuItems = [
    {
      id: "home",
      name: "Home",
      link: "#home",
      icon: Home,
    },
    {
      id: "about",
      name: "About",
      link: "#about",
      icon: User,
    },
    {
      id: "services",
      name: "Services",
      link: "#services",
      icon: Briefcase,
    },
    {
      id: "projects",
      name: "Projects",
      link: "#projects",
      icon: FolderKanban,
    },
    {
      id: "why",
      name: "Why Choose Us",
      link: "#why",
      icon: Star,
    },
    {
      id: "footer",
      name: "Contact Us",
      link: "#footer",
      icon: Phone,
    },
  ];

  /* Active Menu */

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 150;

      let currentSection = "home";

      menuItems.forEach((item) => {
        const section = document.getElementById(item.id);

        if (section && scrollPosition >= section.offsetTop) {
          currentSection = item.id;
        }
      });

      setActiveSection(currentSection);
    };

    window.addEventListener("scroll", handleScroll);

    handleScroll();

    return () =>
      window.removeEventListener("scroll", handleScroll);
  }, []);

  /* Disable Body Scroll */

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "auto";

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [menuOpen]);

  return (
    <>
      {/* ================= HEADER ================= */}

      <header className="fixed top-0 left-0 z-50 w-full border-b border-slate-200/70 bg-white/80 backdrop-blur-xl">

        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">

          {/* Logo */}

          <a
            href="#home"
            className="flex items-center"
          >
            <img
              src={logo}
              alt="Samrat Tech"
              className="h-9 md:h-10"
            />
          </a>

          {/* Desktop Menu */}

          <nav className="hidden min-[900px]:flex items-center gap-8">

            {menuItems.map((item) => (
              <a
                key={item.id}
                href={item.link}
                className={`relative text-[15px] font-medium transition ${
                  activeSection === item.id
                    ? "text-[#b31159]"
                    : "text-slate-700 hover:text-[#b31159]"
                }`}
              >
                {item.name}

                <span
                  className={`absolute -bottom-1 left-0 h-[2px] bg-[#b31159] transition-all duration-300 ${
                    activeSection === item.id
                      ? "w-full"
                      : "w-0"
                  }`}
                />
              </a>
            ))}

          </nav>

          {/* Mobile Menu Button */}

          <button
            onClick={() => setMenuOpen(true)}
            className="min-[900px]:hidden rounded-xl p-2 transition hover:bg-slate-100"
          >
            <Menu size={28} />
          </button>

        </div>

      </header>

      {/* ================= MOBILE MENU ================= */}

      <AnimatePresence>
  {menuOpen && (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[999] bg-white overflow-y-auto overflow-x-hidden"
    >
      {/* Background */}
      <div className="absolute -top-20 right-0 h-40 w-40 rounded-full bg-pink-100 blur-[80px] opacity-40 pointer-events-none" />
      <div className="absolute bottom-0 left-0 h-40 w-40 rounded-full bg-indigo-100 blur-[80px] opacity-40 pointer-events-none" />

      {/* Header */}
      <div className="sticky top-0 z-20 flex items-center justify-between border-b border-slate-200 bg-white/90 backdrop-blur px-5 py-4">

        <img
          src={logo}
          alt="Logo"
          className="w-[250px]"
        />

        <button
          onClick={() => setMenuOpen(false)}
          className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200"
        >
          <X size={18} />
        </button>

      </div>

      {/* Content */}
      <div className="relative px-6 py-8 overflow-x-hidden">
        <div className="space-y-5">

          {menuItems.map((item, index) => (
            <motion.a
              key={item.id}
              href={item.link}
              onClick={() => setMenuOpen(false)}
              initial={{ opacity: 0, x: -25 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{
                delay: index * 0.08,
              }}
              className="group block"
            >
              <div className="flex items-center justify-between">

                <span
                  className={`text-lg font-semibold transition ${
                    activeSection === item.id
                      ? "text-[#b31159]"
                      : "text-slate-800 group-hover:text-[#b31159]"
                  }`}
                >
                  {item.name}
                </span>

                <ChevronRight
                  size={18}
                  className="text-slate-400 group-hover:translate-x-1 transition"
                />

              </div>

              <div
                className={`mt-3 h-px transition-all ${
                  activeSection === item.id
                    ? "bg-[#b31159]"
                    : "bg-slate-200 group-hover:bg-[#b31159]"
                }`}
              />

            </motion.a>
          ))}

        </div>

        {/* CTA */}

        <div className="mt-10 w-full overflow-hidden rounded-2xl bg-slate-900 p-5">

          <h3 className="text-lg font-semibold text-white">
            Let's Work Together
          </h3>

          <p className="mt-2 text-xs leading-5 text-slate-300">
            Need a website, software, networking or AI solution?
            Talk to our experts today.
          </p>

          <button
            onClick={() =>
              window.open(
                "https://wa.me/917897648842?text=Hi%20Samrat%20Tech%20IT%20Solutions,%20I%20need%20a%20free%20consultation.",
                "_blank"
              )
            }
            className="mt-5 w-full rounded-xl bg-[#25D366] py-3 text-sm font-semibold text-white transition hover:bg-[#20ba5a]"
          >
            Chat on WhatsApp
          </button>

        </div>

        <p className="mt-8 text-center text-[11px] text-slate-400">
          © 2026 Samrat Tech IT Solutions
        </p>

      </div>
    </motion.div>
  )}
</AnimatePresence>
</>
);
}

export default Navbar;